/**
 * Eidi envelopes — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidiEnvelopesPack: ToilePack = {
    slug: 'eidi-envelopes',
    category: 'season',
    name: 'Eidi envelopes',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'classic', label: 'Classic envelope', subject: 'a flat envelope net with four flaps and dashed fold lines, the front panel lettered "Eid Mubarak" with a crescent and a blank "For" line beneath'},
        {key: 'pocket', label: 'Money pocket', subject: 'a slim money-pocket template sized for a folded banknote, a pointed top flap with a small star and the word "Eidi" lettered down the front'},
        {key: 'kids', label: 'For kids', subject: 'an envelope net printed with balloons and lanterns, lettered "Your Eidi" on the front with a blank name line and a heart sticker circle on the flap'},
    ],
    styles: [
        {key: 'outline', label: 'Outline', style: 'bold black outlines on empty shapes, the drawings left to colour in', palette: 'black ink only', framing: 'The pieces spaced apart so each can be cut cleanly'},
        {key: 'geometric', label: 'Geometric', style: 'flat geometric print of eight-pointed stars and crescents with solid colour fills and crisp edges', palette: 'teal, gold and white', framing: 'The coloured pieces spaced apart on the white page'},
        {key: 'pastel', label: 'Pastel', style: 'soft flat illustration with rounded shapes and a gentle dotted border on every piece', palette: 'mint, blush, butter yellow and lavender', framing: 'The pieces spaced apart on the white page'},
    ],
    directions: ['for cousins', 'pink theme', 'gold foil'],
    directionHint: 'try: for cousins',
    seo: {
        title: 'Eidi envelope template: classic, money pocket, for kids',
        metaDescription: "Nine printable Eidi envelope templates for Eid money: classic envelopes, slim money pockets and kids' envelopes to fold and glue.",
        h1: 'Eidi envelope templates: classic, money pocket, for kids',
        tagline: 'Nine envelope nets, three designs each in outline, geometric and pastel finishes.',
        intro: "Printable envelope templates for Eidi, the gift of money that elders hand children on Eid morning. The classic envelope is a flat net with four flaps, lettered Eid Mubarak. The money pocket is a slim sleeve sized for a folded banknote, with the word Eidi down the front. The children's envelope is printed with balloons and lanterns and lettered Your Eidi. Type a direction such as for cousins and the envelope is drawn with it.",
        useCases: [
            'Eidi for children on Eid morning',
            'Eidi for nieces and nephews',
            'Eidi for cousins',
            'an envelope for a gift of money',
        ],
        faq: [
            {q: 'How do I make the envelope?', a: 'Cut along the solid lines, fold on the dashed lines, and glue the tabs to close it.'},
            {q: 'What is the money pocket?', a: 'A slim template sized for a folded banknote, with a pointed top flap and a small star.'},
            {q: 'What are the three styles?', a: 'Outline is black ink to colour in, geometric is a flat print with solid colour fills, and pastel is soft flat illustration with a dotted border.'},
            {q: 'Can I ask for a different theme?', a: 'Yes. Type a direction such as gold foil and the envelope is drawn with it. That is a request, not a promised result.'},
        ],
    },
}
